package model.impl

import Flavor
import getPlatform
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.HttpRequestTimeoutException
import io.ktor.client.plugins.timeout
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.bearerAuth
import io.ktor.client.request.get
import io.ktor.client.request.headers
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import io.ktor.http.path
import model.ConsumerInformationV3
import model.DeleteCard
import model.DeleteCardBody
import model.ErrorReportBody
import model.GeoPoint
import model.LoginResponse
import model.MerchantSummaryInformation
import model.NEAR_ME_RADIUS_METERS
import model.NetworkController
import model.Payments
import model.PaymentsAndMerchantInformation
import model.ReferredBusiness
import model.SmsAuthenticationBody
import model.StoresNearbyRequestBody
import model.StoresNearbyResponse
import model.TokenAuthenticationBody
import model.TokenAuthenticationResponse
import model.network.json
import persistence.TimedOutPersistence
import persistence.TokenPersistence
import receipt.Question
import receipt.QuestionAnswer
import receipt.Receipt
import kotlin.time.Clock.System
import kotlin.time.Duration.Companion.hours

class NetworkControllerKtor(
    private val httpClient: HttpClient,
    private val tokenPersistence: TokenPersistence,
    private val timedOutPersistence: TimedOutPersistence,
    private val flavor: Flavor,
    ) : NetworkController {

    class CachedConsumerInformationForToken(val token: String, val consumerInformation: ConsumerInformationV3)

    private var consumerInformationCached: CachedConsumerInformationForToken? = null

    private suspend fun reportError(errorMessage: String) {
        val currentTimeStamp = System.now().toEpochMilliseconds()
        timedOutPersistence.getTimedOutTimeStamp()?.let {
            if (currentTimeStamp.minus(it) < (1.hours.inWholeMilliseconds)) {
                return
            }
        }
        timedOutPersistence.setTimedOutTimeStamp(currentTimeStamp)
        httpClient.post(flavor.enpServiceServerHost) {
            bearerAuth(flavor.token)
            contentType(ContentType.Application.Json)
            url {
                path("/v2/LinxEvents/send-email")
            }
            setBody(json.encodeToString(ErrorReportBody(errorMessage)))
        }
    }


    suspend fun HttpClient.postWithTimeOutError(block: HttpRequestBuilder.() -> Unit): HttpResponse {
        val httpRequestBuilderBlock = HttpRequestBuilder().apply(block)
        try {
            return post(httpRequestBuilderBlock)
        } catch (e: HttpRequestTimeoutException) {
            reportError("${e.message}")
            throw e
        }
    }

    suspend fun HttpClient.getWithTimeOutError(block: HttpRequestBuilder.() -> Unit): HttpResponse {
        val httpRequestBuilderBlock = HttpRequestBuilder().apply(block)
        try {
            return get(httpRequestBuilderBlock)
        } catch (e: HttpRequestTimeoutException) {
            reportError("${e.message}")
            throw e
        }
    }

    override suspend fun getConsumerInformation(): ConsumerInformationV3? {
        val token = tokenPersistence.getToken() ?: throw Exception("Missing Token")
        if (consumerInformationCached?.token != token) {
            val response = httpClient.getWithTimeOutError {
                bearerAuth(token)
                url {
                    path("/v3/consumerInformation")
                }
                headers {
                    append("platform", getPlatform().name)
                }
            }
            if (response.status.value in 200..299) {
                consumerInformationCached =
                    CachedConsumerInformationForToken(token, json.decodeFromString<ConsumerInformationV3>(response.bodyAsText()))
            } else {
                println("Invalid response from server: ${response.bodyAsText()}")
            }
        }
        return consumerInformationCached?.consumerInformation
    }

    override suspend fun getMerchantInformation(consumerLoyaltyProgramGuid: String, forceRefresh: Boolean): Payments {
        val response = httpClient.getWithTimeOutError {
            bearerAuth(tokenPersistence.getToken()!!)
            url {
                path("orderInformation")
                parameters.append("consumerLoyaltyProgramGuid", consumerLoyaltyProgramGuid)
            }
            headers {
                append("platform", getPlatform().name)
            }
        }
        return json.decodeFromString<Payments>(response.bodyAsText())
    }

    override suspend fun getEReceiptInformation(
        orderPaymentGuid: String,
        forceRefresh: Boolean,
        isSimplified: Boolean
    ): Receipt {
        val response = httpClient.getWithTimeOutError {
            contentType(ContentType.Application.Json)
            url {
                path("/v2/eReceipt")
                parameters.append("orderPaymentGuid", orderPaymentGuid)
                parameters.append("isSimplified", isSimplified.toString())
            }
            headers {
                append("platform", getPlatform().name)
            }
        }
        return json.decodeFromString<Receipt>(response.bodyAsText())
    }

    override suspend fun getQuestions(orderPaymentGuid: String): List<Question> {
        val response = httpClient.getWithTimeOutError {
            url {
                path("questions")
                parameters.append("orderPaymentGuid", orderPaymentGuid)
            }
        }
        return json.decodeFromString<List<Question>>(response.bodyAsText())
    }

    override suspend fun answerQuestion(questionAnswer: QuestionAnswer) {
        httpClient.postWithTimeOutError {
            contentType(ContentType.Application.Json)
            url {
                path("answerQuestion")
            }
            setBody(json.encodeToString(questionAnswer))
        }
    }

    override suspend fun sendBusinessEnrollmentInformation(referredBusiness: ReferredBusiness) {
        val response = httpClient.getWithTimeOutError {
            url {
                path("referredBusiness")
                parameters.append("potentialCustomer", json.encodeToString(referredBusiness))
            }
        }
        println(response)
        return Unit
    }

    override suspend fun submitPhoneNumber(phoneNumber: Long): LoginResponse {
        val response = httpClient.postWithTimeOutError {
            contentType(ContentType.Application.Json)
            url {
                path("sms-authentication")
            }
            setBody(json.encodeToString(SmsAuthenticationBody(phoneNumber)))
            headers {
                append("platform", getPlatform().name)
            }
        }
        return when {
            response.status != HttpStatusCode.OK -> LoginResponse.Error(response.status.description)
            response.body<String>().lowercase() == "Ok".lowercase() -> LoginResponse.Submitted()
            else -> LoginResponse.BypassLogin(response.body<TokenAuthenticationResponse>().token)
        }
    }

    override suspend fun getMerchantsNearMe(): List<MerchantSummaryInformation> {
        val response = httpClient.postWithTimeOutError {
            bearerAuth(tokenPersistence.getToken()!!)
            contentType(ContentType.Application.Json)
            url {
                path("merchants-near-me")
            }
            headers {
                append("platform", getPlatform().name)
            }
        }
        return response.body<List<MerchantSummaryInformation>>()
    }

    override suspend fun authenticateGuid(guid: String): String {
        val response = httpClient.postWithTimeOutError {
            contentType(ContentType.Application.Json)
            url {
                path("authentication-response")
            }
            setBody(TokenAuthenticationBody(guid))
            headers {
                append("platform", getPlatform().name)
            }
        }
        return response.body<TokenAuthenticationResponse>().token
    }

    override suspend fun requestAuthenticationFromOrderPaymentGuid(guid: String) {
        val response = httpClient.postWithTimeOutError {
            contentType(ContentType.Application.Json)
            url {
                path("manageAccount")
                parameters.append("orderPaymentGuid", guid)
            }
            headers {
                append("platform", getPlatform().name)
            }
        }
    }

    override suspend fun cancelEnrollment(
        consumerLoyaltyProgramGuid: String,
    ): Boolean {
        val token = tokenPersistence.getToken() ?: throw Exception("Missing Token")
        val response = httpClient.postWithTimeOutError {
            bearerAuth(token)
            url {
                path("cancelEnrollment")
            }
            setBody(consumerLoyaltyProgramGuid)
            headers {
                append("platform", getPlatform().name)
            }
        }
        return when {
            response.status != HttpStatusCode.OK -> throw Exception("Invalid response from server: ${response.bodyAsText()}")
            else -> true
        }
    }

    override suspend fun getDemoAdminCards(consumerLoyaltyProgramGuid: String): List<DeleteCard> {
        val token = tokenPersistence.getToken() ?: throw Exception("Missing Token")
        val response = httpClient.getWithTimeOutError {
            bearerAuth(token)
            url {
                path("adminDemoCard")
                parameters.append("consumerLoyaltyProgramGuid", consumerLoyaltyProgramGuid)
            }
        }

        if (response.status.value !in 200..299) {
            throw Exception("Invalid response from server: ${response.bodyAsText()}")
        }
        return json.decodeFromString<List<DeleteCard>>(response.bodyAsText())
    }

    override suspend fun deleteCard(deleteCardBody: DeleteCardBody): Boolean {
        val token = tokenPersistence.getToken() ?: throw Exception("Missing Token")
        val response = httpClient.postWithTimeOutError {
            contentType(ContentType.Application.Json)
            bearerAuth(token)
            url {
                path("deleteCard")
            }
            setBody(deleteCardBody)
            headers {
                append("platform", getPlatform().name)
            }
        }
        return when {
            response.status != HttpStatusCode.OK -> throw Exception("Invalid response from server: ${response.bodyAsText()}")
            else -> true
        }
    }

    override suspend fun getPaymentsAndConsumerInformation(consumerLoyaltyProgramGuid: String): PaymentsAndMerchantInformation {
        val token = tokenPersistence.getToken() ?: throw Exception("Missing Token")
        val response = httpClient.getWithTimeOutError {
            bearerAuth(token)
            url {
                path("paymentsAndConsumerInformation")
                parameters.append("consumerLoyaltyProgramGuid", consumerLoyaltyProgramGuid)
            }
            headers {
                append("platform", getPlatform().name)
            }
        }
        return json.decodeFromString<PaymentsAndMerchantInformation>(response.bodyAsText())
    }

    override suspend fun postStoresNearMe(geoPoint: GeoPoint): StoresNearbyResponse {
        val token = tokenPersistence.getToken() ?: throw Exception("Missing Token")
        val response = httpClient.postWithTimeOutError {
            contentType(ContentType.Application.Json)
            bearerAuth(token)
            url {
                path("storesNearby")
            }
            headers {
                append("platform", getPlatform().name)
            }
            setBody(StoresNearbyRequestBody(geoPoint, maxAllowedMeters = NEAR_ME_RADIUS_METERS))
            // The Location table is small and the query is bounded to a few km, so a slow answer means something is wrong
            timeout { requestTimeoutMillis = 10_000 }
        }

        if (response.status.value !in 200..299) {
            throw Exception("Nearby stores unavailable (${response.status.value}): ${response.bodyAsText()}")
        }
        return json.decodeFromString<StoresNearbyResponse>(response.bodyAsText())
    }
}